import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { SubscriberFilter, SubscriberListItemDto } from '@tiffinify/contracts';
import { formatShort } from '@tiffinify/core';
import { PageLoader, Badge, Banner, BottomNav, Card, Chip, Chips, Empty, IconBtn, PageTitle, PillAction, RingAvatar, SearchField, Skel, LoadError } from '../components/ui';
import { FlashBanner } from '../lib/flash';
import { formatInr, mealsLabel } from '../lib/format';
import { useRegistrationInbox, useSubscribers } from '../lib/queries';

const FILTERS: Array<{ id: SubscriberFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'ending', label: 'Ending soon' },
  { id: 'paused', label: 'Paused' },
  { id: 'payment', label: 'Payment due' },
  { id: 'past', label: 'Past' },
];

function Row({ i }: { i: SubscriberListItemDto }) {
  const ending = i.tags.includes('ending');
  const due = i.tags.includes('payment');
  const paused = i.tags.includes('paused');
  const color = ending ? 'var(--chilli)' : due ? 'var(--due-ring)' : 'var(--ink)';
  const frac = i.left === null ? 0 : Math.max(0, Math.min(1, (i.daysToEnd ?? 0) / 30));
  return (
    <Link to={`/s/${i.subscriberId}`} className="sub-row">
      <RingAvatar name={i.name} frac={frac} color={color} />
      <span className="mid">
        <span className="name">
          <b>{i.name}</b>
          {i.state === 'closed' ? <Badge kind="end">Closed</Badge> : i.state === 'ended' ? <Badge kind="end">Ended</Badge> : ending ? <Badge kind="end">Ending</Badge> : due ? <Badge kind="due">{formatInr(i.due)} due</Badge> : paused ? <Badge icon="pause">Paused</Badge> : null}
        </span>
        <span className="meta">{[i.area, i.slot ? mealsLabel(i.slot) : null].filter(Boolean).join(' · ')}</span>
      </span>
      {i.state !== 'none' && i.endDate && (
        <span className="end">
          {i.state !== 'closed' && i.state !== 'ended' && <span><b>{i.left ?? 0}</b> left</span>}
          <small>{i.state === 'closed' ? 'Closed' : i.state === 'ended' ? 'Ended' : formatShort(i.endDate)}</small>
        </span>
      )}
    </Link>
  );
}

export function Subscribers() {
  const [q, setQ] = useState('');
  // Search once typing pauses, not on every key.
  const [asked, setAsked] = useState('');
  useEffect(() => { const t = setTimeout(() => setAsked(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  const [filter, setFilter] = useState<SubscriberFilter>('active');
  const list = useSubscribers(asked, filter);
  const registrations = useRegistrationInbox();
  const d = list.data;
  const waiting = registrations.data?.requests.length ?? 0;

  return (
    <>
      <main className="main subscribers">
        <PageTitle
          title="Subscribers"
          sub={
            d
              ? asked
                ? `${d.items.length} ${d.items.length === 1 ? 'customer' : 'customers'} found · ${d.counts.active} active`
                : `${d.counts.active} active ${d.counts.active === 1 ? 'customer' : 'customers'} · ${d.counts.ending} ending soon`
              : <Skel w={170} />
          }
          action={<div className="row">
            <span className="registration-shortcut">
              <IconBtn icon="link" label={waiting ? `Customer registrations, ${waiting} waiting for approval` : 'Share registration link'} to="/registrations" />
              {waiting > 0 && <span className="count-badge" aria-hidden="true">{waiting > 99 ? '99+' : waiting}</span>}
            </span>
            <PillAction label="New" to="/new" />
          </div>}
        />
        <FlashBanner />
        {registrations.data && registrations.data.requests.length > 0 && (
          <Banner action={<Link to="/registrations">Review</Link>}>
            {`${registrations.data.requests.length} customer ${registrations.data.requests.length === 1 ? 'registration is' : 'registrations are'} waiting for review.`}
          </Banner>
        )}
        <SearchField value={q} onChange={setQ} label="Search subscribers" placeholder="Name, phone or area" />
        <Chips label="Filter subscribers" scroll>
          {FILTERS.map((f) => (
            <Chip key={f.id} on={filter === f.id} count={d ? d.counts[f.id] : null} onClick={() => setFilter(f.id)}>{f.label}</Chip>
          ))}
        </Chips>
        {list.isPending ? (
          <PageLoader label="Finding subscribers" />
        ) : list.isError && !d ? (
          <LoadError what="your customers" onRetry={() => void list.refetch()} />
        ) : !d || d.items.length === 0 ? (
          <Card>
            <Empty
              title={
                asked
                  ? 'No one matches'
                  : filter === 'past'
                    ? 'No past customers'
                    : filter === 'ending'
                      ? 'No subscriptions ending soon'
                      : filter === 'paused'
                        ? 'No paused subscribers'
                        : filter === 'payment'
                          ? 'No payments due'
                          : 'No active subscribers'
              }
            >
              {asked
                ? 'Try a different name, phone or area.'
                : filter === 'past'
                  ? 'Closed plans and subscriptions from earlier months will appear here.'
                  : filter === 'active'
                    ? 'Add a subscription to start delivering tiffins.'
                    : 'All clear.'}
            </Empty>
          </Card>
        ) : (
          <Card>{d.items.map((i) => <Row key={i.subscriberId} i={i} />)}</Card>
        )}
      </main>
      <BottomNav />
    </>
  );
}
